import React, { useCallback, useEffect, useState } from 'react'

import { Button, Typography } from 'antd'

import { Icon } from '@iconify/react'

import { getProjectHealthReport } from './reports.service'

import HealthMetricCards from './HealthMetricCards'

import MemberHealthList from './MemberHealthList'

import ProjectHealthContentSkeleton from './ProjectHealthContentSkeleton'

import styles from './ProjectHealthDashboard.module.css'

const { Text } = Typography

const ProjectHealthDashboard = ({ projectId, projectName }) => {
    const [loading, setLoading] = useState(true)

    const [error, setError] = useState(null)

    const [data, setData] = useState(null)

    const [memberSearch, setMemberSearch] = useState('')

    const loadReport = useCallback(async () => {
        if (!projectId) return

        setLoading(true)

        setError(null)

        const response = await getProjectHealthReport(projectId)

        if (response?.success && response?.result) {
            setData(response.result)

            setError(null)
        } else {
            setError(response?.message || 'Could not load project health data')
        }

        setLoading(false)
    }, [projectId])

    useEffect(() => {
        setError(null)

        setData(null)

        loadReport()
    }, [loadReport])

    const isInitialError = !loading && error && !data

    const isRefreshing = loading && !!data

    const showContentSkeleton = loading && !isInitialError

    if (isInitialError) {
        return (
            <div className={styles.stateWrap}>
                <Icon
                    icon="mdi:alert-circle-outline"
                    width={40}
                    height={40}
                    style={{ color: '#ef4444' }}
                />

                <Text className={styles.stateTitle}>Unable to load report</Text>

                <Text className={styles.stateText}>{error}</Text>

                <Button type="primary" onClick={loadReport}>
                    Retry
                </Button>
            </div>
        )
    }

    const displayName = data?.projectName || projectName || 'Project'

    return (
        <div className={styles.dashboard}>
            <header className={styles.dashboardHeader}>
                <div>
                    <p className={styles.dashboardEyebrow}>Reports</p>

                    <h2 className={styles.dashboardTitle}>
                        {displayName} health
                    </h2>

                    <p className={styles.dashboardSubtitle}>
                        Track estimated vs logged effort across your team
                    </p>
                </div>

                <Button
                    type="default"
                    size="small"
                    className={styles.refreshBtn}
                    icon={<Icon icon="mdi:refresh" width={14} height={14} />}
                    onClick={loadReport}
                    loading={isRefreshing}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </header>

            {showContentSkeleton ? (
                <ProjectHealthContentSkeleton />
            ) : (
                <>
                    {error && (
                        <div className={styles.refreshErrorBanner}>
                            <Text className={styles.refreshErrorText}>
                                {error}
                            </Text>

                            <Button
                                type="link"
                                size="small"
                                onClick={loadReport}
                            >
                                Retry
                            </Button>
                        </div>
                    )}

                    <HealthMetricCards data={data} />

                    <MemberHealthList
                        members={data?.members}
                        search={memberSearch}
                        onSearchChange={setMemberSearch}
                    />
                </>
            )}
        </div>
    )
}

export default ProjectHealthDashboard
